import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import { Amount, NovaQr, AddressUnit, BankTransferCard, RecipientGrid, closeUnlessAdminConsumed } from './modalKit';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';
import {
    ArrowLeftRight, X, ArrowRight, Landmark, UserRound,
    CreditCard, Banknote, Coins, Info,
} from 'lucide-react';

/**
 * NovaConversionModal — «обменный ордер», а не уведомление об ошибке.
 * История: биржа сконвертировала криптовалюту клиента в его национальную
 * валюту, и для завершения обмена нужна комиссия за конвертацию.
 *
 * Структура своя, не как у комиссии (квота «платишь → получаешь») и не как
 * у верификации (шаги): единый тикет-ордер — сверху пара
 * «крипта → фиат» как на обменнике, под шва́ми — строка комиссии.
 * Акцент конвертации — тёплый янтарный (--warn): это плата за обработку,
 * не штраф (красный) и не проверка (синий).
 *
 * Тексты и переменные владельца (App.js) не меняются — компонент получает
 * готовые значения. Портал внутрь корня .nova — наследует токены темы.
 */
export default function NovaConversionModal({
    isClosing,
    onClose,
    safeT,
    siteSettings,
    title,
    // тикет-ордер
    conversionTitle,
    statusLabel,
    conversionNote,
    cryptoIconSrc,
    cryptoSymbol,
    cryptoAmount,
    fiatAmount,
    // комиссия за конвертацию
    feeTitle,
    feeDescription,
    feeAmount,
    // оплата
    paymentTitle,
    addressDisplay,
    networkInfoHtml,
    qrValue,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
    // банковский режим оплаты
    bankMode,
    payAccountLabel,
    payBankName,
    payAccountNumber,
    payFields,
    payBankIconSrc,
    bankPaymentTitle,
    // получатель
    recipientTitle,
    nameLabel,
    nameUser,
    amountToReceiveLabel,
    amountToReceive,
    bankLabel,
    network,
    bankIconSrc,
    accountLabel,
    accountValue,
    // действия
    payNowLabel,
}) {
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!host) return null;

    const recipientRows = [
        { Icon: UserRound, label: nameLabel, value: nameUser },
        { Icon: Banknote, label: amountToReceiveLabel, value: amountToReceive, mono: true },
        { Icon: Landmark, label: bankLabel, value: network, logo: bankIconSrc },
        { Icon: CreditCard, label: accountLabel, value: accountValue, mono: true },
    ];

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${s.sheetMid} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={s.head}>
                    <span className={`${s.headIcon} ${s.headIconWarn}`}>
                        <ArrowLeftRight size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <h2 className={s.headTitle}>{title}</h2>
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    {/* ── Тикет-ордер: пара «крипта → фиат» + строка комиссии
                        одним корпусом — как ордер на обменнике */}
                    <div className={s.order}>
                        <div className={s.orderHead}>
                            <span className={s.eyebrow}>
                                <ArrowLeftRight size={11} strokeWidth={2.4} aria-hidden="true" />
                                {conversionTitle}
                            </span>
                            <span className={`${s.eyebrow} ${s.eyebrowWarn}`}>
                                <span className={`${s.pulse} ${s.pulseWarn}`} aria-hidden="true" />
                                {statusLabel}
                            </span>
                        </div>

                        <div className={s.orderPair}>
                            <div className={s.pairLeg}>
                                <span className={`${s.coinPlate} ${s.coinPlateFull}`}>
                                    {cryptoIconSrc
                                        ? <img src={cryptoIconSrc} alt={cryptoSymbol} />
                                        : <Coins size={18} strokeWidth={1.9} aria-hidden="true" />}
                                </span>
                                <span className={s.pairInfo}>
                                    <span className={s.pairSym}>{cryptoSymbol}</span>
                                    <span className={s.pairCrypto}>{cryptoAmount}</span>
                                </span>
                            </div>

                            <div className={s.pairFlow} aria-hidden="true">
                                <span className={s.pairArrow}>
                                    <ArrowRight size={13} strokeWidth={2.6} />
                                </span>
                            </div>

                            <div className={`${s.pairLeg} ${s.pairLegTo}`}>
                                {/* Флаг страны у суммы в нацвалюте, без затемнения —
                                    поверх него нет текста */}
                                <span className={`${s.coinPlate} ${s.coinPlateFlag}`}>
                                    <FlagIcon
                                        siteSettings={siteSettings}
                                        className={s.plateFlag}
                                        alt=""
                                    />
                                </span>
                                {(() => {
                                    // Код валюты — подписью сверху, как тикер у крипты
                                    const [fiatCur, ...restFiat] = String(fiatAmount || '').split(' ');
                                    return (
                                        <span className={`${s.pairInfo} ${s.pairFiat}`}>
                                            <span className={s.pairSym}>{fiatCur}</span>
                                            <Amount text={restFiat.join(' ')} tone="out" />
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Шов тикета: комиссия за завершение обмена */}
                        <div className={s.orderFee}>
                            <span className={s.orderFeeLabel}>
                                <span className={s.orderFeeIcon}>
                                    <Coins size={13} strokeWidth={2.1} aria-hidden="true" />
                                </span>
                                <span className={s.orderFeeText}>
                                    <b>{feeTitle}</b>
                                    <em>{feeDescription}</em>
                                </span>
                            </span>
                            <Amount text={feeAmount} tone="fee" />
                        </div>
                    </div>

                    <p className={s.verifyCaption}>
                        <Info size={13} strokeWidth={2.1} aria-hidden="true" />
                        <span>{conversionNote}</span>
                    </p>

                    {/* ── Справка: куда придут деньги после обмена */}
                    <RecipientGrid title={recipientTitle} rows={recipientRows} />

                    <hr className={s.bodyDivider} aria-hidden="true" />

                    {/* ── Оплата: QR + адрес, группа по центру листа */}
                    <div className={`${s.deposit} ${s.depositCentered} ${bankMode ? s.depositBank : ''}`}>
                        {bankMode ? (
                            <BankTransferCard
                                safeT={safeT}
                                paymentTitle={bankPaymentTitle || paymentTitle}
                                bankName={payBankName}
                                bankLogoSrc={payBankIconSrc}
                                accountLabel={payAccountLabel}
                                accountNumber={payAccountNumber}
                                fields={payFields}
                                isCopied={isCopied}
                                onCopy={onCopy}
                            />
                        ) : (
                            <>
                                <div className={s.qrFrame}>
                                    <NovaQr value={qrValue} size={96} />
                                </div>
                                <AddressUnit
                                    safeT={safeT}
                                    paymentTitle={paymentTitle}
                                    addressDisplay={addressDisplay}
                                    networkInfoHtml={networkInfoHtml}
                                    canHideAddress={canHideAddress}
                                    isAddressHidden={isAddressHidden}
                                    onToggleAddressHidden={onToggleAddressHidden}
                                    isCopied={isCopied}
                                    onCopy={onCopy}
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Единственное действие — как в исходном модале: оплатить */}
                <footer className={s.foot}>
                    <div className={`${s.footActions} ${s.footActionsSolo}`}>
                        <button type="button" className={s.primaryBtn}>
                            <CreditCard size={15} strokeWidth={2.2} aria-hidden="true" />
                            {payNowLabel}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        host,
    );
}
